import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, FileText, PlayCircle, Link as LinkIcon, StickyNote, ListChecks,
  ChevronRight, Folder, Users, ShoppingBag, Megaphone, Truck, ShoppingCart, Briefcase, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Users, ShoppingBag, Megaphone, Truck, ShoppingCart, Briefcase, MapPin, Folder };

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function ResourceForm({ categoryId, onAdded }) {
  const fileRef = useRef();
  const [form, setForm] = useState({
    title: "",
    description: "",
    resource_type: "pdf",
    url_type: "upload",
    url: "",
    body: "",
    checklist_items: [],
  });
  const [uploading, setUploading] = useState(false);
  const [newItem, setNewItem] = useState("");

  const reset = () => {
    setForm({ title: "", description: "", resource_type: "pdf", url_type: "upload", url: "", body: "", checklist_items: [] });
    setNewItem("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, url: data.path, url_type: "upload" }));
      toast.success("Archivo subido");
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    setForm((p) => ({
      ...p,
      checklist_items: [...p.checklist_items, { id: uid("item"), text: newItem.trim(), order: p.checklist_items.length }],
    }));
    setNewItem("");
  };

  const removeItem = (i) => {
    const next = form.checklist_items.slice();
    next.splice(i, 1);
    setForm({ ...form, checklist_items: next });
  };

  const submit = async () => {
    if (!form.title) {
      toast.error("Título requerido");
      return;
    }
    if (form.resource_type === "checklist" && form.checklist_items.length === 0) {
      toast.error("Agrega al menos un paso al checklist");
      return;
    }
    if (form.resource_type !== "note" && form.resource_type !== "checklist" && !form.url) {
      toast.error("Sube un archivo o agrega URL");
      return;
    }
    if (form.resource_type === "note" && !form.body) {
      toast.error("La nota necesita contenido");
      return;
    }
    try {
      await api.post(`/admin/process-categories/${categoryId}/resources`, form);
      toast.success("Material agregado");
      reset();
      onAdded();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const acceptByType = form.resource_type === "pdf" ? "application/pdf" : "video/*";

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-md p-4 space-y-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Nuevo material</p>

      <div className="grid sm:grid-cols-2 gap-2">
        <select
          value={form.resource_type}
          onChange={(e) => setForm({
            ...form,
            resource_type: e.target.value,
            url: "",
            url_type: e.target.value === "link" ? "url" : "upload",
            body: "",
            checklist_items: [],
          })}
          className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
          data-testid="pres-type-select"
        >
          <option value="pdf">PDF</option>
          <option value="video">Video</option>
          <option value="link">Enlace</option>
          <option value="note">Nota / texto</option>
          <option value="checklist">Checklist interactivo</option>
        </select>
        <Input
          placeholder="Título"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="pres-title-input"
        />
      </div>

      <Input
        placeholder="Descripción (opcional)"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
      />

      {form.resource_type === "note" && (
        <Textarea
          placeholder="Contenido de la nota..."
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          rows={4}
          data-testid="pres-body-input"
        />
      )}

      {form.resource_type === "checklist" && (
        <div className="space-y-2 bg-white border border-[#E5E7EB] rounded-md p-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Pasos del checklist</p>
          {form.checklist_items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-2">
              <span className="text-xs text-[#6B7280] w-6">{i + 1}.</span>
              <span className="flex-1 text-sm text-[#1A1A1A]">{it.text}</span>
              <button
                onClick={() => removeItem(i)}
                className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
              placeholder="Escribe un paso y presiona Enter..."
              data-testid="checklist-new-item-input"
            />
            <Button onClick={addItem} variant="outline" className="border-[#E5E7EB]" data-testid="add-checklist-item">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {form.resource_type === "link" && (
        <Input
          placeholder="URL https://..."
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          data-testid="pres-url-input"
        />
      )}

      {(form.resource_type === "pdf" || form.resource_type === "video") && (
        <div className="flex items-center gap-2">
          <select
            value={form.url_type}
            onChange={(e) => setForm({ ...form, url_type: e.target.value, url: "" })}
            className="border border-[#E5E7EB] rounded-md px-3 py-1.5 text-xs bg-white"
          >
            <option value="upload">Subir archivo</option>
            <option value="url">URL externa</option>
          </select>
          {form.url_type === "upload" ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept={acceptByType}
                onChange={handleUpload}
                className="text-sm"
                data-testid="pres-file-input"
              />
              {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
              {form.url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
            </>
          ) : (
            <Input
              placeholder="URL https://..."
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="flex-1"
            />
          )}
        </div>
      )}

      <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white text-sm" data-testid="add-pres-button">
        <Plus className="h-4 w-4 mr-1" /> Agregar material
      </Button>
    </div>
  );
}

export default function AdminProcesses() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [resources, setResources] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/process-categories");
      setCats(r.data);
    } finally {
      setLoading(false);
    }
  };

  const loadCat = async (slug, id) => {
    const r = await api.get(`/process-categories/${slug}`);
    setResources((p) => ({ ...p, [id]: r.data.resources || [] }));
  };

  useEffect(() => { load(); }, []);

  const toggle = async (cat) => {
    if (expanded === cat.id) {
      setExpanded(null);
    } else {
      setExpanded(cat.id);
      if (!resources[cat.id]) await loadCat(cat.slug, cat.id);
    }
  };

  const removeRes = async (cat, rid) => {
    if (!window.confirm("¿Eliminar este material?")) return;
    await api.delete(`/admin/process-categories/${cat.id}/resources/${rid}`);
    toast.success("Material eliminado");
    await loadCat(cat.slug, cat.id);
    await load();
  };

  const ICON_FOR = (k) => ICONS[k] || Folder;

  return (
    <div data-testid="admin-processes-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Folder className="h-3.5 w-3.5" /> Solo administradores
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Procesos por departamento.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Carga PDFs, videos, enlaces, notas y <strong>checklists interactivos</strong> para cada categoría.
          Los vendedores podrán consultarlos y marcar su avance paso a paso.
        </p>
      </div>

      {loading ? (
        <p className="text-[#374151]">Cargando...</p>
      ) : (
        <div className="space-y-4">
          {cats.map((c) => {
            const isOpen = expanded === c.id;
            const Icon = ICON_FOR(c.icon);
            const items = resources[c.id] || [];
            return (
              <div key={c.id} className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`admin-pcat-${c.slug}`}>
                <button
                  onClick={() => toggle(c)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-[#FFFFFF] transition-colors text-left"
                >
                  <div
                    className="h-12 w-12 rounded-md flex items-center justify-center text-white shrink-0"
                    style={{ background: c.color || "#1B2E7A" }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-[#1A1A1A]">{c.name}</h3>
                    <p className="text-xs text-[#737373] truncate">{c.tagline}</p>
                  </div>
                  <span className="text-xs text-[#737373]">
                    {c.resources_count || 0} {c.resources_count === 1 ? "material" : "materiales"}
                  </span>
                  <ChevronRight className={`h-4 w-4 text-[#6B7280] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-[#E5E7EB] p-5 bg-[#FCFAF6] space-y-5">
                    <div className="grid lg:grid-cols-2 gap-5">
                      <ResourceForm
                        categoryId={c.id}
                        onAdded={async () => {
                          await loadCat(c.slug, c.id);
                          await load();
                        }}
                      />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Materiales actuales</p>
                        {items.length === 0 ? (
                          <p className="text-sm text-[#737373]">Aún no hay materiales.</p>
                        ) : (
                          <div className="space-y-2">
                            {items.map((r) => {
                              const RI =
                                r.resource_type === "video" ? PlayCircle :
                                r.resource_type === "pdf" ? FileText :
                                r.resource_type === "note" ? StickyNote :
                                r.resource_type === "checklist" ? ListChecks : LinkIcon;
                              return (
                                <div key={r.id} className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-md p-3">
                                  <RI className="h-4 w-4 text-[#1B2E7A] shrink-0" strokeWidth={1.5} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{r.title}</p>
                                    <p className="text-xs text-[#6B7280]">
                                      {r.resource_type.toUpperCase()}
                                      {r.resource_type === "checklist" && ` · ${r.checklist_items?.length || 0} pasos`}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => removeRes(c, r.id)}
                                    className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md"
                                    data-testid={`del-pres-${r.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/procesos/${c.slug}`}
                      className="inline-flex items-center gap-1 text-xs text-[#1B2E7A] hover:underline"
                    >
                      Ver vista de vendedor →
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
